import { OrderPaymentRedirectType, OrderPaymentResponse } from "../types/order";
import React, { useRef, useState } from "react";
import { RouteNames } from "../routes";
import appToastr from "../services/appToastr";
import { collectAllErrorToString } from "../helpers/network";
import { useHistory } from "react-router-dom";
import OrderConfirmOfferModal from "../screens/Order/OrderIndex/OrderConfirmOfferModal/OrderConfirmOfferModal";
import ErrorModal from "../components/ui/modals/ErrorModal";
import { translate } from "../helpers/translatioHelpers";
import accountOrdersDetailApi from "../api/account-orders-detail-api";
import orderApi from "../api/orderApi";

type PaymentResponseWithNestedData = OrderPaymentResponse & {
  data?: OrderPaymentResponse;
};

type ConfirmOrderResponse = {
  success?: boolean;
  uuid?: string;
  message?: string;
  data?: {
    success?: boolean;
    uuid?: string;
    message?: string;
  };
};

const setPaymentDebugData = (key: string, value: unknown) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({
      timestamp: new Date().toISOString(),
      value,
    }));
  } catch (error) {
    console.error('[payment-debug] sessionStorage error', error);
  }
};

const PAYMENT_REDIRECT_DELAY_MS = 1000;

const usePaymentOnline = () => {
  const history = useHistory();
  const [orderIdForConfirm, setOrderIdForConfirm] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPaymentUnavilable, setShowPaymentUnavailable] = useState<boolean>(false);

  const [showIndicator, setShowIndicator] = useState<boolean>(false);
  const [paymentData, setPaymentData] = useState<PaymentResponseWithNestedData | null>(null);
  const paymentDataRef = useRef<PaymentResponseWithNestedData | null>(null);
  const pendingRedirectRef = useRef<OrderPaymentRedirectType | null>(null);
  const [paymentRedirectData, setPaymentRedirectData] = useState<OrderPaymentRedirectType | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const redirectToPayment = (link: string) => {
    window.location.href = link;
  };

  React.useEffect(() => {
    if (paymentRedirectData?.method === 'post' && paymentRedirectData?.link) {
      setTimeout(() => {
        formRef.current?.submit();
      }, 100);
    }
  }, [paymentRedirectData]);

  const startRedirect = (redirectData?: OrderPaymentRedirectType | null) => {
    if (!redirectData?.link) return;

    setPaymentDebugData('payment-debug:startRedirect', redirectData);
    console.log('[payment-debug] startRedirect', redirectData);

    if (redirectData.method === 'post') {
      setTimeout(() => {
        setPaymentRedirectData(redirectData);
      }, PAYMENT_REDIRECT_DELAY_MS);
    } else {
      setTimeout(() => {
        redirectToPayment(redirectData.link);
      }, PAYMENT_REDIRECT_DELAY_MS);
    }
  };

  const getResolvedPaymentData = (currentPaymentData?: PaymentResponseWithNestedData | null) => {
    if (!currentPaymentData) return null;

    return currentPaymentData.data || currentPaymentData;
  };

  const makePayment = (id) => {
    setShowIndicator(true);

    accountOrdersDetailApi.goToPayment(id).then((res)=>{
      paymentDataRef.current = res;
      setPaymentData(res);
      checkPaymentDataFromServer(res);
    }).catch(()=>{

    }).finally(()=>{
      setShowIndicator(false)
    });
  }


  const checkPaymentDataFromServer = (paymentData: PaymentResponseWithNestedData) => {
    const resolvedPaymentData = getResolvedPaymentData(paymentData);
    const redirectData = resolvedPaymentData?.redirect as OrderPaymentRedirectType;
    const isSuccess = resolvedPaymentData?.success;
    console.log(redirectData, paymentData);

    if (!resolvedPaymentData) return;

    //Todo: need to refactor
    if (redirectData?.link) {
      console.log(redirectData);
      if (redirectData?.publicOffer) {
        setPaymentRedirectData(null);
        pendingRedirectRef.current = redirectData;
        setOrderIdForConfirm(redirectData?.id);
        setShowConfirmModal(true);
      } else {
        startRedirect(redirectData);
      }
    } else {
      if (isSuccess) {
        history.push(`${RouteNames.ORDER_SUCCESS}?can-confirm=${resolvedPaymentData?.canConfirm}`);
        sessionStorage.setItem('paymentServerData', JSON.stringify(paymentData));
      } else {
        setShowPaymentUnavailable(true);
      }
    }
  };

  const onOrderConfirm = async () => {
    if (!orderIdForConfirm || isConfirming) return;

    setIsConfirming(true);

    try {
      const response = await orderApi.confirmOrder(orderIdForConfirm) as ConfirmOrderResponse;
      const resolvedResponse = response?.data || response;
      setPaymentDebugData('payment-debug:offerAgreeResponse', response);
      console.log('[offer-agree] response', response);
      const isConfirmSuccess = resolvedResponse?.success === true;

      if (isConfirmSuccess) {
        setShowConfirmModal(false);
        const payData = getResolvedPaymentData(paymentDataRef.current);
        const redirectData = pendingRedirectRef.current || (payData?.redirect as OrderPaymentRedirectType);
        startRedirect(redirectData);
      } else {
        if (resolvedResponse?.message) {
          appToastr.error(`Помилка при оплаті: ${resolvedResponse.message}`);
        }
        setShowPaymentUnavailable(true);
      }
    } catch (error) {
      setPaymentDebugData('payment-debug:offerAgreeError', error);
      console.error('[offer-agree] error', error);
      appToastr.error(`Помилка при оплаті ${collectAllErrorToString(error)}`);
    } finally {
      setIsConfirming(false);
    }
  };

  return {
    showIndicator,
    makePayment,
    AdditionalComponents: () => (
      <>
        {paymentRedirectData?.link && (
          <form action={paymentRedirectData.link} method="post" target="_self" ref={formRef}>
            <input type="hidden" name="bodyRequest"
                   value={JSON.stringify(paymentRedirectData?.data?.bodyRequest)} />
            <input type="hidden" name="typeRequest" value="json" />
            <input type="submit" value="Submit" style={{ display: 'none' }} />
          </form>
        )}

        {showConfirmModal && (
          <OrderConfirmOfferModal
            onClose={() => {
              if (isConfirming) return;
              setPaymentRedirectData(null);
              pendingRedirectRef.current = null;
              setOrderIdForConfirm(null);
              setShowConfirmModal(false);
            }}
            onConfirm={onOrderConfirm}
            isConfirming={isConfirming}
          />
        )}

        {showPaymentUnavilable && (
          <ErrorModal
            onClose={() => {setShowPaymentUnavailable(false);}}
            text={translate('Платіж неможливий')}
          />
        )}
      </>
    )
  }
}

export default usePaymentOnline;
